"use strict";

import WMSCapabilities from "ol/format/WMSCapabilities";
import jQuery from 'jquery';

/**
 * @classdesc
 * Openlayers-Wrapper des WMS-Providers.
 *
 * @class
 * @extents gc.gis.GCWrappable
 * @memberof gc.gis
 * @author Anton Popov <a.popov@brain-scc.de>
 *
 * @param {gc.gis.util.GCRequestManager} requestManager
 * @param {gc.gis.map.GCAbstractMap} map
 */
gc.gis.OLWMSProvider = function(requestManager, map)
{
	this.available_versions = [
		{
			label: '1.3.0',
			value: '1.3.0'
		},
		{
			label: '1.1.1',
			value: '1.1.1'
		},
		{
			label: '1.1.0',
			value: '1.1.0'
		},
		{
			label: '1.0.0',
			value: '1.0.0'
		}
	];
	gc.gis.map.GCAbstractWMSProvider.call(this, requestManager, map);

	this.WGS84_PROJ_CODE = 'EPSG:4326';
};
gc.gis.OLWMSProvider.prototype = Object.create(gc.gis.map.GCAbstractWMSProvider.prototype);

/**
 * @inheritDoc
 */
gc.gis.OLWMSProvider.prototype.getCapabilities = function(url, onResponse)
{
	gc.gis.assert.isSet(url);

	const self = this;
	const capabilitiesUrl = this._buildCapabilitiesUrl(url);

	this._requestManager.ajax({
		label: 'Lade WMS-Capabilities ... ',
		context: this,
		url: capabilitiesUrl,
		useOGCProxy: true,
		success: function(data)
		{
            const wms = new WMSCapabilities();
            const capabilities = self._processCapabilities(wms.read(data));

            if(typeof onResponse === 'function')
            {
				onResponse.call(self, capabilities);
            }
            else
            {
				throw new gc.gis.error.GCError('No callback defined to process WMS capabilities! Doing nothing.');
            }
		},
		error: function(err)
		{
			throw new gc.gis.error.GCError(`Failed to retrieve wms capabilities: ${capabilitiesUrl}`, err);
		}
	});
};

/**
 * @return {string}
 * @private
 */
gc.gis.OLWMSProvider.prototype._buildCapabilitiesUrl = function(url)
{
	const capabilitiesUrl = gc.gis.GCUrl.fromString(url);

	for (let q in capabilitiesUrl.queryObject)
	{
		if (capabilitiesUrl.queryObject.hasOwnProperty(q))
		{
			if (q.toUpperCase() === 'SERVICE' || q.toUpperCase() === 'REQUEST')
			{
				delete capabilitiesUrl.queryObject[q];
			}
		}
	}
	capabilitiesUrl.queryObject.SERVICE = 'WMS';
	capabilitiesUrl.queryObject.REQUEST = 'GetCapabilities';

	return gc.gis.GCUrl.toString(capabilitiesUrl);
};

/**
 * @param {Object} capabilities An object representing the WMS capabilities.
 */
gc.gis.OLWMSProvider.prototype._processCapabilities = function(capabilities)
{
	switch(capabilities.version)
	{
		case '1.0.0':
			return this._processCapabilities_1_0_0(capabilities);
		case '1.1.0':
			return this._processCapabilities_1_1_0(capabilities);
		case '1.1.1':
			return this._processCapabilities_1_1_1(capabilities);
		case '1.3.0':
			return this._processCapabilities_1_3_0(capabilities);
	}
};

/**
 * @param {Object} capabilities An object representing the WMS capabilities.
 */
gc.gis.OLWMSProvider.prototype._processCapabilities_1_0_0 = function(capabilities)
{
	return this._processCapabilities_1_3_0(capabilities);
};

/**
 * @param {Object} capabilities An object representing the WMS capabilities.
 */
gc.gis.OLWMSProvider.prototype._processCapabilities_1_1_0 = function(capabilities)
{
	return this._processCapabilities_1_3_0(capabilities);
};

/**
 * @param {Object} capabilities An object representing the WMS capabilities.
 */
gc.gis.OLWMSProvider.prototype._processCapabilities_1_1_1 = function(capabilities)
{
	return this._processCapabilities_1_3_0(capabilities);
};

/**
 * @param {Object} capabilities An object representing the WMS capabilities.
 * @return {Object}
 */
gc.gis.OLWMSProvider.prototype._processCapabilities_1_3_0 = function(capabilities)
{
	if(!this._isVersionSupported(capabilities.version))
	{
		throw new gc.gis.error.GCError(`WMS Version not supported: ${capabilities.version}`);
	}

	// Ergebnisobjekt
	const parsedCapabilities = {
		version:     capabilities.version,
		title:       capabilities.Service.Title || '',
		abstract:	 capabilities.Service.Abstract || '',
		resource:    capabilities.Service.OnlineResource,
		formats:     [],
		infoFormats: [],
		layers:      []
	};

	parsedCapabilities.formats = this._parseGetMapFormats(capabilities);
	parsedCapabilities.infoFormats = this._parseGetFeatureInfoFormats(capabilities);
	parsedCapabilities.requests = this._parseRequests(capabilities);
	parsedCapabilities.layers = this._parseLayers(capabilities);

	return parsedCapabilities;
};

gc.gis.OLWMSProvider.prototype._parseGetMapFormats = function(capabilities)
{
	const formats = [];
	const capFormatsList = capabilities.Capability.Request.GetMap.Format;

	for (let i = 0, ii = capFormatsList.length; i < ii; i++)
	{
		const capFormat = capFormatsList[i];
		formats.push({
			label: capFormat,
			value: capFormat
		});
	}

	return formats;
};

gc.gis.OLWMSProvider.prototype._parseGetFeatureInfoFormats = function(capabilities)
{
	if (capabilities.Capability.Request.GetFeatureInfo)
	{
		return capabilities.Capability.Request.GetFeatureInfo.Format;
	}

	return [];
};

gc.gis.OLWMSProvider.prototype._parseRequests = function(capabilities)
{
	const requests = {};
	let requestParams, binding;

	Object.getOwnPropertyNames(capabilities.Capability.Request).forEach(function(requestName) {
		requests[requestName] = {};
		requestParams = capabilities.Capability.Request[requestName];

		for (let i = 0, ii = requestParams.DCPType.length; i < ii; i++)
		{
			binding = requestParams.DCPType[i];
			if (binding.HTTP && binding.HTTP.Get)
			{
				requests[requestName].GET = binding.HTTP.Get.OnlineResource;
			}

			if (binding.HTTP && binding.HTTP.Post)
			{
				requests[requestName].POST = binding.HTTP.Get.OnlineResource;
			}
		}
	});

	return requests;
};

gc.gis.OLWMSProvider.prototype._parseLayers = function(capabilities)
{
	const capLayer = capabilities.Capability.Layer;
	let parsedLayer = undefined;

	if (capLayer)
	{
		parsedLayer = this._getCapabilitiesLayer(capLayer);
		this._parseChildLayers(capLayer, parsedLayer);
	}

	return parsedLayer ? [parsedLayer] : [];
};

gc.gis.OLWMSProvider.prototype._parseChildLayers = function(capParentLayer, parsedParentLayer)
{
	const parsedChildLayers = [];
	const capChildLayerList = capParentLayer.Layer || [];

	for (let i = 0, ii = capChildLayerList.length; i < ii; i++)
	{
		const capChildLayer = capChildLayerList[i];
		const parsedChildLayer = this._getCapabilitiesLayer(capChildLayer);

		parsedChildLayer.crs.concat(parsedParentLayer.crs);
		parsedChildLayer.crs = gc.gis.GCUtil.uniqueArray(parsedChildLayer.crs);
		parsedChildLayer.parentLayer = parsedParentLayer;
		parsedChildLayers.push(parsedChildLayer);

		if (capChildLayer.Layer)
		{
			this._parseChildLayers(capChildLayer, parsedChildLayer);
		}
	}

	if (parsedChildLayers.length > 0)
	{
		parsedParentLayer.layers = parsedChildLayers;
	}
};

gc.gis.OLWMSProvider.prototype._isVersionSupported = function(version)
{
	for (let i=0; i<this.available_versions.length; i++)
	{
		if (version === this.available_versions[i].value)
		{
			return true;
		}
	}

	return false;
};

gc.gis.OLWMSProvider.prototype._getCapabilitiesLayer = function(capLayer)
{
	return {
		name:        capLayer.Name,
		label:       capLayer.Title,
		minScale:    capLayer.MinScaleDenominator,
		maxScale:    capLayer.MaxScaleDenominator,
		crs:		 this._parseLayerCrs(capLayer),
		boundingBox: this._parseLayerBoundingBox(capLayer),
		attribution: this._parseLayerAttribution(capLayer),
		styles:      this._parseLayerStyles(capLayer),
		queryable:	 capLayer.queryable,
		opaque:		 capLayer.opaque,
		cascaded:	 capLayer.cascaded
	};
};

gc.gis.OLWMSProvider.prototype._parseLayerStyles = function(capLayer)
{
	// der OpenLayers Capabilities-Parser liefert teilweise Styles mehrfach (BUG?)
	const parsedStyles = [];
	const stylesUnique = {};
	const capStyleList = capLayer.Style || [];

	for (let i = 0, ii = capStyleList.length; i < ii; i++)
	{
		if (stylesUnique[capStyleList[i].Name]) continue;
		stylesUnique[capStyleList[i].Name] = capStyleList[i];
	}

	let styleName;
	let parsedStyle;
	for (styleName in stylesUnique)
	{
		parsedStyle = {
			name: styleName,
			label: stylesUnique[styleName].Title
		};
		if (Array.isArray(stylesUnique[styleName].LegendURL) && stylesUnique[styleName].LegendURL.length > 0)
		{
			parsedStyle.legend = {
				url: stylesUnique[styleName].LegendURL[0].OnlineResource,
				format: stylesUnique[styleName].LegendURL[0].Format,
				size: stylesUnique[styleName].LegendURL[0].size
			}
		}
		parsedStyles.push(parsedStyle);
	}

	return parsedStyles;
};

gc.gis.OLWMSProvider.prototype._parseLayerAttribution = function(capLayer)
{
	const attribution = [];

	if (capLayer.Attribution)
	{
		attribution.push({
			label: capLayer.Attribution.Title,
			url: capLayer.Attribution.OnlineResource
		});
	}
	else if (capLayer.AuthorityURL)
	{
		for (let i=0, ii = capLayer.AuthorityURL.length; i < ii; i++)
		{
			attribution.push({
				label: capLayer.AuthorityURL[i].name,
				url:   capLayer.AuthorityURL[i].OnlineResource
			});
		}
	}

	return attribution;
};

gc.gis.OLWMSProvider.prototype._parseLayerBoundingBox = function(capLayer)
{
	const boundingBoxes = [];
	if (capLayer.EX_GeographicBoundingBox)
	{
		const geographicBox = capLayer.EX_GeographicBoundingBox;
		boundingBoxes.push({
			extent: [
				geographicBox[0],
				geographicBox[1],
				geographicBox[2],
				geographicBox[3]
			],
			crs: this.WGS84_PROJ_CODE
		});
	}

	const capBoxList = capLayer.BoundingBox || [];
	for (let i = 0, ii = capBoxList.length; i < ii; i++)
	{
		if (!capBoxList[i].crs) continue;
		if (this.WGS84_PROJ_CODE === capBoxList[i].crs.toUpperCase()) continue;

		boundingBoxes.push({
			extent: capBoxList[i].extent,
			crs: capBoxList[i].crs.toUpperCase()
		});
	}

	return boundingBoxes;
};

gc.gis.OLWMSProvider.prototype._parseLayerCrs = function(capLayer)
{
	const crs = [];
	const capCrsList = capLayer.CRS || [];

	for (let i = 0, ii = capCrsList.length; i < ii; i++)
	{
		crs.push(capCrsList[i]);
	}

	return crs;
};

/**
 * @inheritDoc
 */
gc.gis.OLWMSProvider.prototype.findLayer = function(capabilities, layerName)
{
	return this._findLayerRecursive(capabilities.layers, layerName);
};

/**
 * @inheritDoc
 */
gc.gis.OLWMSProvider.prototype.findLayers = function(capabilities, layerNames)
{
	const capLayers = []
	let locatedLayer;

	for (let i = 0, ii = layerNames.length; i < ii; i++)
	{
		locatedLayer = this._findLayerRecursive(capabilities.layers, layerNames[i]);
		if (locatedLayer)
		{
			capLayers.push(locatedLayer);
		}
	}
	return capLayers;
};

gc.gis.OLWMSProvider.prototype._findLayerRecursive = function(layerTree, layerName)
{
	for (let i = 0, ii = layerTree.length; i < ii; i++)
	{
		if (layerTree[i].name === layerName)
		{
			return layerTree[i];
		}

		if (layerTree[i].layers)
		{
			const locatedLayer = this._findLayerRecursive(layerTree[i].layers, layerName);
			if (locatedLayer)
			{
				return locatedLayer;
			}
		}
	}

	return null;
};

/**
 *
 * @inheritDoc
 */
gc.gis.OLWMSProvider.prototype.getLayerConfig = function(capabilities, layers, opt_styles, opt_options)
{
	opt_styles = opt_styles || [];
	opt_options = opt_options || {};

	const url = opt_options.resource || this._tryDeterminGetMapUrl(capabilities);
	let format = opt_options.format;
	const tiled = opt_options.tiled === true;
	const preferredCRS = opt_options.preferredCRS;
	const version = opt_options.version || capabilities.version;
	const infoFormats = capabilities.infoFormats;

	if (!format && capabilities.formats.length > 0)
	{
		format = capabilities.formats[0];
	}

	const capLayers = [];
	const validLayerTitels = [];
	const validLayerNames = [];
	const validStyles = [];

	for (let i = 0, ii = layers.length; i < ii; i++)
	{
		const layerName = layers[i];
		const styleName = i < opt_styles.length ? opt_styles[i] : '';
		const capLayer = this.findLayer(capabilities, layerName);

		if (capLayer)
		{
			capLayers.push(capLayer);
			validLayerTitels.push(capLayer.label);
			validLayerNames.push(layerName);
			validStyles.push(styleName);
		}
	}

	const minScale = this._determinMinScaleForLayerList(capLayers);
	const maxScale = this._determinMaxScaleForLayerList(capLayers);
	const attribution = this._determinAttributionForLayerList(capLayers);
	let crs = this._determinCRS(capLayers, preferredCRS);

	if (!crs)
	{
		if ('1.3.0' !== version && preferredCRS)
		{
			// Hack: der OL Capabilities-Parser unterstützt nur die WMS-Version 1.3.0 um dennoch zu ermöglichen, dass Layer von WMS < 1.3
			// geladen werden können, wird hier, sofern vorhanden, das bevorzugte CRS genutzt, ohne zu prüfen ob der WMS dies unterstützt
			crs = preferredCRS;
		}
		else
		{
			throw new gc.gis.error.GCError('Der WMS unterstützt nicht die Koordinatenreferenzssysteme des Viewers.');
		}
	}

	const extent = this._determinExtentForLayerList(capLayers, crs);

	return {
		isInternal:   			false,
		type:         			'wms',
		originalType: 			'external_wms',
		name:         			validLayerNames.join(','),
		label:        			validLayerTitels.join(' / '),
		style:		  			validStyles.join(','),
		minScale:     			minScale,
		maxScale:     			maxScale,
		attribution:  			attribution,
		version:      			version,
		tiled:        			tiled,
		baseUrl:      			url,
		transparent:  			'1',
		format:       			format,
		extent:		  			extent,
		projection:   			crs,
		availableInfoFormats:	infoFormats
	};
};

gc.gis.OLWMSProvider.prototype._tryDeterminGetMapUrl = function(capabilities)
{
	if (capabilities.requests.GetMap && capabilities.requests.GetMap.GET)
	{
		return capabilities.requests.GetMap.GET;
	}
	throw new gc.gis.error.GCError('WMS does not support GetMap-Request via HTTP-Get.');
};

gc.gis.OLWMSProvider.prototype._determinMinScaleForLayerList = function(capLayers)
{
	let capLayer;
	let minScale = undefined;

	for (let i = 0, ii = capLayers.length; i < ii; i++)
	{
		capLayer = capLayers[i];

		// wenn ein Layer kein Maßstabsbeschränkung nach unten hat => dann gibt es insgesamt keine Maßstabsbeschränkung nach unten
		if (capLayer.minScale === undefined)
		{
			return undefined;
		}

		if (minScale === undefined)
		{
			minScale = capLayer.minScale;
		}
		else
		{
			minScale = Math.min(minScale, capLayer.minScale);
		}
	}

	return minScale;
};

gc.gis.OLWMSProvider.prototype._determinMaxScaleForLayerList = function(capLayers)
{
	let capLayer;
	let maxScale = undefined;

	for (let i = 0, ii = capLayers.length; i < ii; i++)
	{
		capLayer = capLayers[i];

		// wenn ein Layer kein Maßstabsbeschränkung nach oben hat => dann gibt es insgesamt keine Maßstabsbeschränkung nach oben
		if (capLayer.maxScale === undefined)
		{
			return undefined;
		}

		if (maxScale === undefined)
		{
			maxScale = capLayer.maxScale;
		}
		else
		{
			maxScale = Math.max(maxScale, capLayer.maxScale);
		}
	}

	return maxScale;
};

gc.gis.OLWMSProvider.prototype._determinAttributionForLayerList = function(capLayers)
{
	const attributions = [];
	let layerAttributions;
	let capLayer;

	for (let i = 0, ii = capLayers.length; i < ii; i++)
	{
		layerAttributions = [];
		capLayer = capLayers[i];

		for (let j = 0, jj = capLayer.attribution.length; j < jj; j++)
		{
			const attribution = capLayer.attribution[j];
			const url = attribution.url;
			const label = attribution.label;

			if (url)
			{
				layerAttributions.push(`<a href="${url}">${label}</a>`);
			}
			else
			{
				layerAttributions.push(label);
			}
		}
		attributions.push(layerAttributions.join(', '));
	}

	return attributions.join(' / ');
};

gc.gis.OLWMSProvider.prototype._determinCRS = function(capLayers, opt_preferredCRS)
{
	let crs;
	const crsList = this._determinCRSForLayerList(capLayers);

	if (opt_preferredCRS)
	{
		const index = crsList.indexOf(opt_preferredCRS);
		if (index > -1 && this._map.supportsCRS(crsList[index]))
		{
			crs = crsList[index];
		}
	}

	for (let i = 0, ii = crsList.length; !crs && i < ii; i++)
	{
		if (this._map.supportsCRS(crsList[i]))
		{
			crs = crsList[i];
		}
	}

	return crs;
}

gc.gis.OLWMSProvider.prototype._determinCRSForLayerList = function(capLayers)
{
	let crsList = undefined;
	let capLayer;
	for (let i = 0, ii = capLayers.length; i < ii; i++)
	{
		capLayer = capLayers[i];

		if (crsList === undefined)
		{
			crsList = capLayer.crs;
		}
		else
		{
			crsList = gc.gis.GCUtil.intersectArray(crsList, capLayer.crs)
		}
	}

	return crsList || [];
};

gc.gis.OLWMSProvider.prototype._determinExtentForLayerList = function(capLayers, crs)
{
	let extent = undefined;
	let layerExtent;

	for (let i = 0, ii = capLayers.length; i < ii; i++)
	{
		layerExtent = this._determinExtentForLayer(capLayers[i], crs);
		if (layerExtent)
		{
			extent = !extent ? layerExtent : gc.gis.GCUtil.expandExtent(extent, layerExtent);
		}
	}

	return extent;
};

gc.gis.OLWMSProvider.prototype._determinExtentForLayer = function(capLayer, crs)
{
	let extent = undefined;
	let boundingBox;
	let wgs84Extent;

	for (let i = 0, ii = capLayer.boundingBox.length; i < ii; i++)
	{
		boundingBox = capLayer.boundingBox[i];
		if (crs === boundingBox.crs)
		{
			return boundingBox.extent;
		}
		if (this.WGS84_PROJ_CODE === boundingBox.crs)
		{
			wgs84Extent = boundingBox.extent;
		}
	}

	if (wgs84Extent)
	{
		extent = this._map.transformExtent(wgs84Extent, this.WGS84_PROJ_CODE, crs);
	}

	return extent;
};

/**
 * @param {gc.gis.layer.GCRasterLayer} layer Raster-Layer-Instanz
 * @return {jQuery} jQuery-Element
 */
gc.gis.OLWMSProvider.prototype.getLegendGraphic = function(layer)
{
	gc.gis.assert.isInstance(layer, gc.gis.layer.GCRasterLayer);

	const legendContainer = jQuery('<div>', {
		'class': 'legend-graphic',
		html: '<p class="legend-graphic-loading"><em>Lade Legendengrafik... </em></p>'
	});
	this._loadLegendGraphic(layer, legendContainer);

	return legendContainer;
};

gc.gis.OLWMSProvider.prototype._loadLegendGraphic = function(layer, container)
{
	if (layer.isInternal())
	{
		this._loadLegendGraphicForInternalLayer(layer, container);
	}
	else
	{
		this._loadLegendGraphicForExternalLayer(layer, container);
	}
};

gc.gis.OLWMSProvider.prototype._loadLegendGraphicForInternalLayer = function(layer, container)
{
	let url = layer.getUrl();
	const version = layer.getVersion() || '1.3.0';
	const url_params = {
		SERVICE: 'WMS',
		REQUEST: 'GetLegendGraphic',
		LAYER: layer.getName(),
		FORMAT: 'image/png',
		VERSION: version
	};

	if ('1.3.0' === version)
	{
		url_params.SLD_VERSION = '1.1.0';
	}

	url = gc.gis.GCUrl.buildUrl(url, url_params);
	this._loadAndMountElementLegendGraphic(layer, url, container);
};

gc.gis.OLWMSProvider.prototype._loadLegendGraphicForExternalLayer = function(layer, container)
{
	const self = this;
	this.getCapabilities(layer.getUrl(), function (capabilities) {
		const legendGraphics = self._determineLegendGraphicFromCapabilities(capabilities, layer);
		for (let i = 0, ii = legendGraphics.length; i < ii; i++)
		{
			self._loadAndMountElementLegendGraphic(layer, legendGraphics[i].url, container);
		}
	});
};

gc.gis.OLWMSProvider.prototype._determineLegendGraphicFromCapabilities = function(capabilities, layer)
{
	const layerNames = layer.getName().split(',');
	const layerStyles = layer.getStyle().split(',');
	const legendGraphics = [];

	let layerName, styleName;
	let capLayer;
	for (let i = 0, ii = layerNames.length; i < ii; i++)
	{
		layerName = layerNames[i];
		styleName = layerStyles.length < i ? layerStyles[i] : undefined;

		capLayer = this.findLayer(capabilities, layerName);
		if (!capLayer || !capLayer.styles) continue;

		let legend = this._determineLegendGraphicFromStyleList(capLayer.styles);
		if (!legend) continue;
		legendGraphics.push(legend);
	}

	return legendGraphics;
};

gc.gis.OLWMSProvider.prototype._determineLegendGraphicFromStyleList = function(capStyles, styleName)
{
	let capStyle;
	for (let i = 0, ii = capStyles.length; i < ii; i++)
	{
		capStyle = capStyles[i];
		if (styleName && styleName === capStyle.name)
		{
			if (capStyle.legend)
			{
				return capStyle.legend;
			}
		}
		else if (!styleName && 'default' === capStyle.name)
		{
			if (capStyle.legend)
			{
				return capStyle.legend;
			}
		}
	}

	if (capStyles.length > 0 && capStyles[0].legend)
	{
		return capStyles[0].legend;
	}

	return undefined;
};

gc.gis.OLWMSProvider.prototype._loadAndMountElementLegendGraphic = function(layer, url, container)
{
	const self = this;
	const legendElement = jQuery('<img>', {
		alt: 'Legendengrafik',
		src: url
	});

	legendElement.on('load', function(req) {
		container.children().not('img').remove();
		if (!this.complete || typeof this.naturalWidth == 'undefined' || this.naturalWidth === 0)
		{
			container.append(self._createElementNoLegendGraphicAvailable());
		}
		else
		{
			container.append(legendElement);
		}
	});

	legendElement.on('error', function(a) {
		gc.gis.iface.log.error(self, new gc.gis.error.GCError('Fehler beim Laden der Legendengrafik für "'+ layer.getLabel() + '".'));
		container.children().remove();
		container.append(self._createElementErrorLoadingLegendGraphic());
	});
};

gc.gis.OLWMSProvider.prototype._createElementNoLegendGraphicAvailable = function()
{
	return jQuery('<em>', {
		text: 'Keine Legendengrafik verfügbar.'
	});
};

gc.gis.OLWMSProvider.prototype._createElementErrorLoadingLegendGraphic = function()
{
	return jQuery('<em>', {
		'class': 'gc-error',
		text: 'Fehler beim Laden der Legendengrafik.'
	});
};

/**
 * @param {gc.gis.layer.GCRasterLayer} layer Raster-Layer-Instanz
 * @return {string}
 */
gc.gis.OLWMSProvider.prototype.getGetMapUrl = function(layer)
{
	gc.gis.assert.isInstance(layer, gc.gis.layer.GCRasterLayer);

	// TODO ggf. weitere Parameter z.B. BBOX bei Bedarf unterstützen
	const params = {
		SERVICE: 'WMS',
		VERSION: layer.getVersion() || '1.3.0',
		REQUEST: 'GetMap',
		LAYERS: layer.getName(),
		FORMAT: layer.getFormat()
	};

	return gc.gis.GCUrl.buildUrl(layer.getUrl(), params);
};
